import React, { ReactNode, useCallback, useState } from 'react';
import { createCtx } from '@shared/util/context';
import { useToggle } from '@shared/util/hooks';
import { useUpgrade } from '../metricsCounter/container';
import { UpgradeContent, UpgradeContentProps, UpgradeModal } from '.';

export interface ShowModalOptions {
  title: string;
  description: string;
  header?: ReactNode;
  hideUpgradeButton?: boolean;
  onLearnMore?: () => void;
}
interface UpgradeModalAPI {
  show(data: ShowModalOptions): void;
}

export const [useUpgradeModal, UpgradeModalContextProvider] = createCtx<UpgradeModalAPI>();

interface Props {
  children: ReactNode;
}

export function UpgradeModalProvider({ children }: Props) {
  const [modalProps, setModalProps] = useState<ShowModalOptions>({
    title: '',
    description: '',
    onLearnMore: undefined,
    header: undefined,
    hideUpgradeButton: false,
  });
  const upgrade = useUpgrade();
  const { isOn, toggleOn, toggleOff } = useToggle();

  const show = useCallback<UpgradeModalAPI['show']>(
    ({ title, description, header = undefined, hideUpgradeButton, onLearnMore }) => {
      setModalProps({ title, description, header, hideUpgradeButton, onLearnMore });
      toggleOn();
    },
    [toggleOn],
  );

  return (
    <UpgradeModalContextProvider value={{ show }}>
      {children}
      <UpgradeModal
        visible={isOn}
        onClose={toggleOff}
        onUpgrade={modalProps.hideUpgradeButton ? () => {} : upgrade}
        {...modalProps}
      />
    </UpgradeModalContextProvider>
  );
}

export function UpgradeInlineContent({
  hideUpgradeButton,
  ...props
}: Omit<UpgradeContentProps, 'onUpgrade'> & { hideUpgradeButton?: boolean }) {
  const upgrade = useUpgrade();
  return <UpgradeContent {...props} onUpgrade={hideUpgradeButton ? undefined : upgrade} />;
}
